import queryString from 'query-string';

const queryParamsMatch = (routeQueryParams, locationQueryParams) => {
  const keys = Object.keys(routeQueryParams);
  return keys.length === 0 || !keys.find(key => routeQueryParams[key] !== locationQueryParams[key]);
};

const matchPathAndQuery = (routePath, routeQuery, locationPathname, locationQuery) => {
  return (locationPathname.indexOf(routePath) === 0) && queryParamsMatch(routeQuery, locationQuery);
};

class Route {
  constructor(routeConfig, parent, children) {
    Object.assign(this, routeConfig);
    this.defaultMatchPath = routeConfig.matchPath;
    if (!this.path) this.path = '';
    this.parent = parent;
    this.matchLocation = this.matchLocation.bind(this);
    this.setChildren(children);
    this.contextSubscribers = [];
    this.bindedSetContext = this.setContext.bind(this);
    this.badgeSubscribers = [];
    const sessionStorage = {};
    if (!this.context) {
      this.context = {};
    } else if (this.context.filter) {
      this.context.defaultFilter = this.context.filter;
    }
    this.context.getStorageItem = key => sessionStorage[key] || null;
    this.context.setStorageItem = (key, item) => { sessionStorage[key] = item; };
  }

  getPath() {
    return this.path;
  }

  /**
   * Get path from root ancestor to this route
   */
  getFullPath(query) {
    if (this.parent) {
      return `${this.parent.getFullPath(query)}${this.getPath(query)}`;
    }
    return this.path;
  }

  findMatchingRoute(locationPathname, locationQuery) {
    const query = {};
    let path = this.getPath(query);
    if ((locationPathname.indexOf(path) !== 0) || !queryParamsMatch(query, locationQuery)) {
      return null;
    }
    if (!this.children || !this.children.length) {
      return this;
    }
    path = locationPathname.substring(path.length);
    if (!path || !path.length) {
      return null;
    }
    let matchedRoute = null;
    this.children.find((childRoute) => {
      matchedRoute = childRoute.findMatchingRoute(path, locationQuery);
      return matchedRoute;
    });
    return matchedRoute;
  }

  getRoute(id) {
    return (this.children || []).find(route => route.id === id);
  }

  match(location, query) {
    let locationPathname = location.pathname;
    if (this.parent) {
      const parentQuery = {};
      const parentPath = this.parent.getFullPath(parentQuery);
      if (!matchPathAndQuery(parentPath, parentQuery, locationPathname, query)) {
        return null;
      }
      locationPathname = locationPathname.substring(parentPath.length);
    }
    return this.findMatchingRoute(locationPathname, query);
  }

  matchLocation(match, location) {
    return Boolean(this.match(location, location.search && queryString.parse(location.search) || {}));
  }

  setLastVisitedLocation(location) {
    const lastVisitedLocation = {
      pathname: location.pathname,
      query: location.query,
    };
    if (this.parent) {
      this.parent.setLastVisitedLocation(lastVisitedLocation);
    }
    this.lastVisitedLocation = lastVisitedLocation;
  }

  getLastVisitedLocation() {
    return this.lastVisitedLocation;
  }

  findDefaultLeafRoute(props) {
    const defaultChild = this.findDefaultChildRoute(props);
    return defaultChild && defaultChild.findDefaultLeafRoute(props) || this;
  }

  findDefaultChildRoute() {
    return this.children && this.children.length && this.children[0];
  }

  getDefaultPath(props, query) {
    const leafRoute = this.findDefaultLeafRoute(props);
    return leafRoute.getFullPath(query);
  }

  url(props) {
    const lastVisitedLocation = this.getLastVisitedLocation();
    if (lastVisitedLocation) {
      const query = Object.assign({}, lastVisitedLocation.query);
      // todo
      // https://github.com/Gurobi/tuc-projects/issues/64
      // Hack for ensuring the currently selected scenario is in the query string.
      // Should replace lastVisitedLocation with lastVisitedChildPath but it requires
      // to process the child route from a given path.
      this.getFullPath(query);
      const url = `${lastVisitedLocation.pathname}?${queryString.stringify(query)}`;
      return url;
    }
    const query = {};
    const path = this.getDefaultPath(props, query);
    if (Object.keys(query).length) {
      const url = `${path}?${queryString.stringify(query)}`;
      return url;
    }
    return path;
  }

  getProperty(name) {
    const value = this[name];
    if (value !== undefined) {
      return value;
    }
    return this.parent ? this.getProperty(name) : undefined;
  }

  setChildren(children) {
    this.children = children || [];
    this.children.forEach((childRoute) => {
      const r = childRoute;
      r.parent = this;
      r.updateMatchPath();
    });
    this.updateMatchPath();
  }

  updateMatchPath() {
    // getMatchPath will be used as the path parameter of react-router route matching this route.
    // https://github.com/pillarjs/path-to-regexp/tree/v1.7.0 provides syntax for regexp matching path.
    // and test regexp at: http://forbeslindesay.github.io/express-route-tester/
    const query = {};
    const path = this.getFullPath(query);
    if (this.defaultMatchPath) {
      this.matchPath = this.defaultMatchPath;
    } else {
      this.matchPath = (this.matchChildPaths || (this.children && this.children.length))
        && `${path}/:foo*` || path;
    }
  }

  getSelectableRoutes() {
    return [this];
  }

  // Context
  /**
   * Returns contextual data, such as filtering string, used by app bar and main component
   * associated with this route.
   * Initial value of this context is provided with the context configuration provided
   * with the constructor.
   */
  getContext() {
    return this.context;
  }

  setContext(context) {
    const oldContext = this.context;
    this.context = context;
    this.contextSubscribers.forEach(listener => listener(context, oldContext, this));
  }

  subscribeContextChange(listener) {
    this.contextSubscribers.push(listener);
  }

  unsubscribeContextChange(listener) {
    const index = this.contextSubscribers.indexOf(listener);
    if (index >= 0) {
      this.contextSubscribers.splice(index, 1);
    }
  }

  getBadge(props) {
    return this.badgeGen ? this.badgeGen(props) : this.badge;
  }

  setBadge(badge) {
    if (badge !== this.badge) {
      const oldBadge = this.badge;
      this.badge = badge;
      this.badgeSubscribers.forEach(sub => sub(badge, oldBadge, this));
    }
  }

  subscribeBadgeChange(sub) {
    this.badgeSubscribers.push(sub);
  }

  unsubscribeBadgeChange(sub) {
    const index = this.badgeSubscribers.indexOf(sub);
    if (index >= 0) {
      this.badgeSubscribers.splice(index, 1);
    }
  }
}

export default Route;
